import React, { useContext } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { DeviceContext } from '../../../contexts';
import { AppCheckbox, AppKeyboardDatePicker } from '../../../components/inputs';
import { MAIN_CONTAINER_ID, RTW_LABELS, RTW_VALUES } from '../../../constants';
import { RTWVerification } from '../../../types';
import { formatDate } from '../../../utils/dates';
import { ControlledSelect } from '../../../components/controlled-inputs/ControlledSelect';
import { AppButton } from '../../../components/AppButton';

// expiring is computed by the backend, rejected is retired — neither can be set manually
const RTW_STATUS_OPTIONS = [
  RTW_VALUES.UNVERIFIED,
  RTW_VALUES.INDEFINITE,
  RTW_VALUES.LIMITED,
  RTW_VALUES.NO_RTW,
].map(value => ({ value, name: RTW_LABELS[value] }));

// a retired status (e.g. rejected) has no option, so the select starts empty and must be re-picked
const getStatusOption = (value: RTW_VALUES = RTW_VALUES.UNVERIFIED) => {
  return RTW_STATUS_OPTIONS.find(option => option.value === value);
};

interface Form {
  verified: boolean;
  check_date?: string | null;
  expiration_date?: string | null;
  status?: { value: RTW_VALUES; name: string };
}

interface Props {
  open: boolean;
  loading: boolean;
  handleClose: Function;
  updateRtw: (data: Partial<RTWVerification>) => void;
  rtwVerification?: RTWVerification;
}

export const RtwManualVerificationDialog: React.FC<Props> = ({
  open,
  handleClose,
  loading,
  rtwVerification,
  updateRtw,
}) => {
  const { isMobile } = useContext(DeviceContext);
  const methods = useForm<Form>({
    defaultValues: {
      verified: !!rtwVerification?.verified,
      check_date: rtwVerification?.check_date || null,
      expiration_date: rtwVerification?.expiration_date || null,
      status: getStatusOption(rtwVerification?.status),
    },
  });
  const { control, watch } = methods;
  const isLimited = watch('status')?.value === RTW_VALUES.LIMITED;

  const onSubmit = async (data: Form) => {
    const status = data.status?.value;
    updateRtw({
      ...data,
      status,
      // only a limited right to work has an end date
      expiration_date: status === RTW_VALUES.LIMITED ? data.expiration_date || null : null,
      check_date: data.check_date || null,
    });
  };

  return (
    <Dialog
      fullScreen={isMobile}
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      onClose={() => handleClose()}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogTitle>Verify Right to Work manually</DialogTitle>

          <DialogContent>
            <Box width={isMobile ? '100%' : 400}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Controller
                  name="check_date"
                  control={control}
                  render={props => (
                    <AppKeyboardDatePicker
                      margin="normal"
                      value={props.field.value || null}
                      onChange={data => props.field.onChange(formatDate(data))}
                      label="Date of Check"
                      format="dd/MM/yyyy"
                      fullWidth
                      disableFuture
                    />
                  )}
                />
              </MuiPickersUtilsProvider>

              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Controller
                  name="expiration_date"
                  control={control}
                  rules={{
                    validate: value =>
                      !isLimited || !!value || 'Date of Expiry is required for a limited right to work',
                  }}
                  render={({ field, fieldState }) => (
                    <AppKeyboardDatePicker
                      margin="normal"
                      value={(isLimited && field.value) || null}
                      onChange={data => field.onChange(formatDate(data))}
                      label="Date of Expiry"
                      format="dd/MM/yyyy"
                      fullWidth
                      disabled={!isLimited}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </MuiPickersUtilsProvider>

              <ControlledSelect
                margin="normal"
                label="Status"
                name="status"
                items={RTW_STATUS_OPTIONS}
                multiple={false}
                rules={{ required: 'Status is required' }}
              />

              <Box display="flex" justifyContent="flex-end" marginTop={1}>
                <Controller
                  name="verified"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <AppCheckbox
                          onChange={e => field.onChange(e.target.checked)}
                          color="primary"
                          checked={field.value}
                        />
                      }
                      label="Verified"
                    />
                  )}
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button style={{ width: 106 }} size="large" variant="outlined" onClick={() => handleClose()}>
              Close
            </Button>
            <AppButton
              style={{ width: 106 }}
              size="large"
              type="submit"
              color="primary"
              variant="contained"
              loaderVariant="paper"
              loading={loading}>
              Save
            </AppButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
